import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateMe } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Mail, Shield, ShieldCheck, Save, Eye, EyeOff, Banknote } from 'lucide-react';
import { toast } from 'sonner';

const MyAccount = () => {
  const { user, login: updateUserContext } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Profile form
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    hourly_rate: user?.hourly_rate ?? 300,
  });
  
  // Password form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileData.name.trim()) {
      toast.error('Jméno je povinné');
      return;
    }
    
    setLoading(true);
    try {
      const response = await updateMe({ name: profileData.name, hourly_rate: parseFloat(profileData.hourly_rate) || 0 });
      // Update local storage and context
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      updateUserContext(localStorage.getItem('token'), updatedUser);
      toast.success('Profil byl aktualizován');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při aktualizaci profilu');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password) {
      toast.error('Zadejte aktuální heslo');
      return;
    }
    
    if (!passwordData.new_password) {
      toast.error('Zadejte nové heslo');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Nové heslo musí mít alespoň 6 znaků');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Hesla se neshodují');
      return;
    }
    
    setLoading(true);
    try {
      await updateMe({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success('Heslo bylo změněno');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při změně hesla');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="my-account-page">
      {/* Header */}
      <div>
        <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">Můj účet</h1>
        <p className="font-serif text-gray-600 mt-1">Nastavení profilu a zabezpečení</p>
      </div>

      {/* Account Info Card */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 flex items-center justify-center border-2 border-black ${user?.is_admin ? 'bg-[#006CB6]' : 'bg-[#F4E600]'}`}>
            {user?.is_admin ? (
              <ShieldCheck size={32} className="text-white" />
            ) : (
              <User size={32} />
            )}
          </div>
          <div>
            <h2 className="font-oswald text-2xl font-bold uppercase">{user?.name}</h2>
            <p className="font-serif text-gray-600">{user?.email}</p>
            <span className={`mt-1 inline-block px-2 py-1 text-xs font-oswald uppercase ${user?.is_admin ? 'bg-[#006CB6] text-white' : 'bg-gray-100 text-gray-800'}`}>
              {user?.is_admin ? 'Administrátor' : 'Uživatel'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Settings */}
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-2 mb-6">
            <User size={24} className="text-[#006CB6]" />
            <h2 className="font-oswald text-xl font-bold uppercase tracking-tight">Osobní údaje</h2>
          </div>
          
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide">Email</Label>
              <div className="flex items-center gap-2 p-3 bg-gray-50 border-2 border-gray-200">
                <Mail size={18} className="text-gray-400" />
                <span className="font-serif text-gray-600">{user?.email}</span>
              </div>
              <p className="text-xs text-gray-500 font-serif">Email nelze změnit</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name" className="font-oswald uppercase text-xs tracking-wide">Jméno</Label>
              <Input
                id="name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                className="border-2 border-gray-200 focus:border-black rounded-none"
                data-testid="profile-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hourly_rate" className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                <Banknote size={12} /> Hodinová sazba (Kč/h)
              </Label>
              <Input
                id="hourly_rate"
                type="number"
                min="0"
                max="10000"
                step="10"
                value={profileData.hourly_rate}
                onChange={(e) => setProfileData({ ...profileData, hourly_rate: e.target.value })}
                className="border-2 border-gray-200 focus:border-black rounded-none"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
              data-testid="save-profile-btn"
            >
              <Save size={18} className="mr-2" />
              {loading ? 'Ukládání...' : 'Uložit změny'}
            </Button>
          </form>
        </div>

        {/* Password Change */}
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-2 mb-6">
            <Lock size={24} className="text-[#006CB6]" />
            <h2 className="font-oswald text-xl font-bold uppercase tracking-tight">Změna hesla</h2>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password" className="font-oswald uppercase text-xs tracking-wide">
                Aktuální heslo
              </Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black"
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password" className="font-oswald uppercase text-xs tracking-wide">
                Nové heslo
              </Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none pr-10"
                  placeholder="Minimálně 6 znaků"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="font-oswald uppercase text-xs tracking-wide">
                Potvrzení nového hesla
              </Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="border-2 border-gray-200 focus:border-black rounded-none"
                placeholder="Zopakujte nové heslo"
                data-testid="confirm-password-input"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white font-oswald uppercase tracking-wide border-2 border-black rounded-none hover:bg-gray-900 transition-all"
              data-testid="change-password-btn"
            >
              <Lock size={18} className="mr-2" />
              {loading ? 'Měním heslo...' : 'Změnit heslo'}
            </Button>
          </form>
        </div>
      </div>

      {/* Permissions Overview */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center gap-2 mb-6">
          <Shield size={24} className="text-[#006CB6]" />
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight">Moje oprávnění</h2>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {Object.entries(user?.permissions || {}).map(([key, value]) => (
            <span
              key={key}
              className={`px-4 py-2 text-sm font-oswald uppercase border-2 ${
                value 
                  ? 'bg-green-50 text-green-700 border-green-300' 
                  : 'bg-gray-50 text-gray-400 border-gray-200 line-through'
              }`}
            >
              {key === 'clients' ? 'Klienti' :
               key === 'projects' ? 'Projekty' :
               key === 'tasks' ? 'Úkoly' :
               key === 'time_tracking' ? 'Čas' :
               key === 'revenue' ? 'Tržby' :
               key === 'users' ? 'Uživatelé' : key}
            </span>
          ))}
        </div>
        
        {!user?.is_admin && (
          <p className="mt-4 text-sm text-gray-500 font-serif">
            Pro změnu oprávnění kontaktujte administrátora.
          </p>
        )}
      </div>
    </div>
  );
};

export default MyAccount;
